import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup 
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  prjDeptHeaderTyps,
  prjSalesHeaderTyps,
  prjDeptProfitHeaders,
  prjTargetRateProfitHeaderTyps,
  prjExTargetRateHeaderTyps,
  prjRealSalesProfitHeaderTyps,
  prjStatProfitHeaderTyps,
} from '../../utils/forecastStaticData';
import ProfitStaticsTeamRows from './ProfitStaticsTeamRows';

const useStyles = makeStyles({
  table: {
    '& .MuiTableCell-root': {
      border: '1px solid rgba(224, 224, 224, 1)',
      whiteSpace: 'nowrap', // 데이터 영역은 한 줄 유지
      padding: '6px 4px',
    },
    '& .MuiTableCell-head': {
      fontWeight: 'bold',
      whiteSpace: 'pre-line', // 헤더 내 \n 개행 처리
      lineHeight: 1.1,        // 개행 시 줄 간격 최적화
    },
  },
});

const ProfitStaticsTeamTable = ({ data, tableRef, selectPrjTyp, strMonth, endMonth }) => {
  const classes = useStyles();
  const [showKorean, setShowKorean] = useState(false);

  const getBgColor = (id) => {
    if (id?.includes('STAT')) return '#b0deff';
    if (id === 'OA') return '#e3f2fd'; 
    if (id === 'HM') return '#fff3e0'; 
    if (id === 'BUDGETAMT') return '#e8f5e9';
    if (id === 'SELLINGEX') return '#fff3e0';

    return '#f5f5f5'; 
  };

  const getHeaderTyps = (id) => {
    if (id === 'EXSTATRATE') return prjExTargetRateHeaderTyps;
    if (id === 'STATRATE') return prjTargetRateProfitHeaderTyps;
    if (id?.includes('STAT')) return prjStatProfitHeaderTyps;
    if (id === 'OA') return prjRealSalesProfitHeaderTyps;
    if (id === 'HM') return prjDeptHeaderTyps;
    return prjSalesHeaderTyps;
  };

  const currentHeaders = prjDeptProfitHeaders(strMonth, endMonth, '매출');
  const filteredHeaders = currentHeaders.filter((header) => 
    header.id.includes('STAT') ? selectPrjTyp === 'A' : true
  );

  const totalColCount = 2 + filteredHeaders.reduce((acc, header) => {
    const isSingleCol = header.id === 'SELLINGEX' || header.id === 'BUDGETAMT';
    return acc + (isSingleCol ? 1 : getHeaderTyps(header.id).length);
  }, 0);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, px: 1 }}>
        <ToggleButtonGroup
          value={showKorean}
          exclusive
          onChange={(e, nextValue) => { if (nextValue !== null) setShowKorean(nextValue); }}
          size="small"
          sx={{
            height: '32px',
            '& .MuiToggleButton-root': {
              fontSize: '0.75rem', fontWeight: 700, color: '#455a64', px: 1.5, border: '1px solid #cfd8dc', 
              '&.Mui-selected': { backgroundColor: '#1967d2', color: 'white' },
            },
          }}
        >
          <ToggleButton value={false}>숫자 적용</ToggleButton>
          <ToggleButton value={true}>한글 적용</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" sx={{ color: '#455a64', fontWeight: 700, fontSize: '0.75rem', marginLeft: 'auto' }}>
          (단위: {showKorean ? '한글 혼용' : '원'})
        </Typography>
      </Box>

      <Paper sx={{ my: 1, width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ width: '100%', maxHeight: '70vh', overflow: 'auto' }}>
          <Table 
            size="small" 
            className={classes.table} 
            ref={tableRef} 
            stickyHeader 
            sx={{ 
              tableLayout: 'fixed', 
              width: 'max-content', 
              minWidth: '100%' 
            }}
          >
            <colgroup>
              <col style={{ width: '90px', minWidth: '90px' }} />
              <col style={{ width: '90px', minWidth: '90px' }} />
              {filteredHeaders.flatMap((header) => {
                const isSingleCol = header.id === 'SELLINGEX' || header.id === 'BUDGETAMT';
                
                if (isSingleCol) {
                  return <col key={`col-${header.id}`} style={{ width: '100px', minWidth: '100px' }} />;
                }

                const headerTyp = getHeaderTyps(header.id);
                return headerTyp.map((typ, idx) => {
                  const isPercentage = 
                    typ.id?.toLowerCase().includes('rate') || 
                    typ.id === 'salesProfitMargin' || 
                    typ.name?.includes('율') ||
                    typ.name?.includes('%');

                  // 퍼센트 컬럼 너비 40px로 축소
                  const colWidth = isPercentage ? '40px' : '100px';
                  return (
                    <col 
                      key={`col-${header.id}-${idx}`} 
                      style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }} 
                    />
                  );
                });
              })}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell align="center" rowSpan={2} sx={{ bgcolor: '#f5f5f5', zIndex: 12, fontSize: '0.85rem', fontWeight: 'bold' }}>본부</TableCell>
                <TableCell align="center" rowSpan={2} sx={{ bgcolor: '#f5f5f5', zIndex: 12, fontSize: '0.85rem', fontWeight: 'bold' }}>팀</TableCell>
                {filteredHeaders.map((header) => {
                  const isSingleCol = header.id === 'SELLINGEX' || header.id === 'BUDGETAMT';
                  const headerTyp = getHeaderTyps(header.id);
                  return (
                    <TableCell 
                      key={header.val} 
                      align="center" 
                      rowSpan={isSingleCol ? 2 : 1}
                      colSpan={isSingleCol ? 1 : headerTyp.length} 
                      sx={{ 
                        bgcolor: getBgColor(header.id), 
                        fontWeight: 800, 
                        fontSize: '0.9rem', 
                        zIndex: 11,
                        whiteSpace: 'pre-line' // \n 개행 처리
                      }}
                    >
                      {header.name}
                    </TableCell>
                  );
                })}
              </TableRow>
              <TableRow>
                {filteredHeaders.map((header) => {
                  if (header.id === 'SELLINGEX' || header.id === 'BUDGETAMT') return null;
                  
                  const headerTyp = getHeaderTyps(header.id);
                  return headerTyp.map((typ, index) => {
                    const isPercentage = typ.id?.toLowerCase().includes('rate') || typ.id === 'salesProfitMargin' || typ.name?.includes('율') || typ.name?.includes('%');
                    return (
                      <TableCell 
                        key={`${header.id}-${index}`} 
                        align="center" 
                        sx={{ 
                          bgcolor: getBgColor(header.id), 
                          fontSize: '0.8rem', 
                          fontWeight: 700,
                          // 퍼센트 컬럼은 좌우 패딩을 0으로 설정해 공간 확보
                          padding: isPercentage ? '4px 0px' : '6px 2px',
                          zIndex: 11,
                          whiteSpace: 'pre-line' // \n 개행 처리
                        }}
                      >
                        {typ.name}
                      </TableCell>
                    );
                  });  
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {!data || data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={totalColCount} align="center" sx={{ py: 5, color: '#1a73e8', fontWeight: 600 }}>
                    데이터가 없습니다.
                  </TableCell>
                </TableRow>
              ) : (
                <ProfitStaticsTeamRows 
                  useKorean={showKorean} 
                  selectPrjTyp={selectPrjTyp} 
                  headerRows={filteredHeaders} 
                  data={data} 
                />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
export default ProfitStaticsTeamTable;
